/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  computed,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, filter } from 'rxjs';
import { saveClickLog } from '../../../core/clicks/click.actions';
import { ClickLog, ClickOperation } from '../../../core/clicks/click.models';
import { Category } from '../../../domain/category/category.model';
import { selectCategory } from '../../../domain/category/category.selectors';
import { loadCityByPrefix } from '../../../domain/city/city.actions';
import { City } from '../../../domain/city/city.model';
import { selectCityByPrefix } from '../../../domain/city/city.selectors';
import { Province } from '../../../domain/province/province.model';
import { selectProvince } from '../../../domain/province/province.selectors';
import { Specialty } from '../../../domain/specialty/specialty.model';
import { selectSpecialty } from '../../../domain/specialty/specialty.selectors';
import { updateUser } from '../../../domain/user/user.actions';
import { ListaGruppiEnum, User, UserDTO, ListaGruppi } from '../../../domain/user/user.model';
import { selectUser, selectUserOperationState } from '../../../domain/user/user.selectors';
import { loadDropDownData } from '../../user-form/user-form.actions';
import { UserForm, UserFormType } from '../../user-form/user-form.model';
import { DNOperationState, evaluateBooleanFromString, getDateInUTC } from '../../util/util';
import { DottnetDialogComponent } from '../dn-dialog.component';
import { UserFormComponent } from '../../user-form/user-form.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatIconButton } from '@angular/material/button';
import { Location, NgStyle, AsyncPipe } from '@angular/common';
import { ProfessionType } from '../../../domain/profession/profession.model';
import { selectProfession } from '../../../domain/profession/profession.selectors';
import { AgenasDisciplineType } from 'src/app/domain/agenas-discipline/agenas-discipline.model';
import { selectAgenasDiscipline } from 'src/app/domain/agenas-discipline/agenas-discipline.selectors';
import { toSignal } from '@angular/core/rxjs-interop';

export interface WallDialogData {
  listaGruppi: ListaGruppi[];
}

@Component({
  selector: 'dottnet-mat-dialog-wall',
  templateUrl: 'wall-dialog.component.html',
  styleUrls: ['wall-dialog.component.scss'],
  imports: [
    SpinnerComponent,
    UserFormComponent,
    NgStyle,
    AsyncPipe,
    MatIconButton,
    MatDialogTitle,
    MatDialogContent,
    FaIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WallDialogComponent
  extends DottnetDialogComponent<WallDialogComponent>
  implements OnInit, OnDestroy
{
  readonly UserFormType = UserFormType;

  // Single signals
  readonly categories = toSignal(this.store.select(selectCategory), { initialValue: null });

  readonly specialties = toSignal(this.store.select(selectSpecialty), { initialValue: null });

  readonly professionTypes = toSignal(this.store.select(selectProfession), { initialValue: null });

  readonly agenasDisciplineTypes = toSignal(this.store.select(selectAgenasDiscipline), {
    initialValue: null,
  });

  readonly provinces = toSignal(this.store.select(selectProvince), { initialValue: null });

  readonly cities = toSignal(this.store.select(selectCityByPrefix), { initialValue: null });

  // User settings
  user$: Observable<UserDTO> = new Observable();

  // ListaGruppi
  listaGruppi: ListaGruppiEnum[] = [];

  // User vars
  password: string = '';
  idAnagrafica: number = -1;
  privacy: boolean = false;
  userEmail = signal<string>('');
  isSubmitting = signal<boolean>(false);

  // Subs
  userSub: Subscription = new Subscription();
  userOperationSub: Subscription = new Subscription();

  // Flag to track closes initiated by submitForm and avoid double-logging
  private closedManually = false;

  //To signal
  showPersonal = signal<boolean>(false);
  showProfessional = signal<boolean> (false);
  showConsents = signal<boolean>(false);
  showEmail = signal<boolean>(false);
  showPwd = signal<boolean>(false);
  showEcm  = signal<boolean>(false);
  ecmChecked  = signal<boolean>(false);
  enableEmailField = signal<boolean>(false);
  prefillEmail = signal<boolean>(true);

  readonly hasFormGroups = computed(() =>
    this.showPersonal() || this.showProfessional() || this.showConsents() || this.showEcm()
  );

  constructor(
    dialogRef: MatDialogRef<WallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private wallData: WallDialogData,
    @Inject(PLATFORM_ID) platformId: Object,
    public store: Store,
    private ts: TranslateService,
    private location: Location,
  ) {
    super(dialogRef, undefined, store, platformId);
  }

  ngOnInit(): void {
    if (this.wallData.listaGruppi.find(elem => elem.mandatory)) {
      this.dialogRef.disableClose = true;
    }



    const clickLog: ClickLog = <ClickLog>{};
    clickLog.clickLogOperationID = ClickOperation.AperturaCompletaProfilo;
    clickLog.content = JSON.stringify(this.wallData.listaGruppi);
    clickLog.message = 'Wall aperto';
    clickLog.method = 'GET';
    clickLog.outcome = true;
    this.store.dispatch(saveClickLog({ clickLog }));


    this.listaGruppi = this.getListaGruppiFromArray(this.wallData.listaGruppi);


    if (
      this.listaGruppi.includes(ListaGruppiEnum.Professional) ||
      this.listaGruppi.includes(ListaGruppiEnum.ECM)
    ) {
      this.store.dispatch(loadDropDownData());
    }

    this.user$ = this.store.select(selectUser);

    this.userSub = this.user$.pipe(filter((utente: UserDTO) => !!utente)).subscribe((utente) => {
      this.password = utente.password;
      this.idAnagrafica = utente.idAnagrafica;
      this.privacy = utente.privacy;
      this.userEmail.set(utente.email);
      this.prefillEmail.set(utente.correttezza !== 5);

      this.listaGruppi?.map(elem => {
        switch (elem) {
          case ListaGruppiEnum.Personal:
            this.showPersonal.set(true) ;
            this.showEmail.set(true);
            if (this.password && this.password.length === 0) {
              this.showPwd.set(true);
              // this.dialogRef.disableClose = true;   // potrei disabilitare la chiusura della dialog se mi manca la pwd. Ma per ora non lo facciamo
            }
            break;
          case ListaGruppiEnum.Professional:
            this.showProfessional.set(true) ;
            break;
          case ListaGruppiEnum.Consents:
            this.showConsents.set(true);
            break;
          case ListaGruppiEnum.ECM:
            this.showEcm.set(true) ;
            this.ecmChecked.set(true);
            break;
          case ListaGruppiEnum.Credentials:
            this.showEmail.set(true);
            this.enableEmailField.set(true);
            break;
        }
      });
    });


    this.userOperationSub = this.store.select(selectUserOperationState)
      .pipe(filter(userOp => userOp == DNOperationState.LOADING))
      .subscribe(() => this.isSubmitting.set(true))

    this.dialogRef.beforeClosed().subscribe(() => {
      if (!this.closedManually) {
        const clickLog: ClickLog = <ClickLog>{};
        clickLog.clickLogOperationID = ClickOperation.ChiusuraFormCompletaProfilo;
        clickLog.content = JSON.stringify(this.wallData.listaGruppi);
        clickLog.message = 'Wall chiuso';
        clickLog.method = 'CLICK';
        clickLog.outcome = false;
        this.store.dispatch(saveClickLog({ clickLog }));
      }
    });

  }

  ngOnDestroy(): void {
    if (this.userSub) this.userSub.unsubscribe();
    if (this.userOperationSub) this.userOperationSub.unsubscribe();
    // Don't forget to complete the subject when the component is destroyed
  }

  getTitle(): string {
    if (this.showEmail() && !this.hasFormGroups()) {
      // No email on file → "enter your email" copy; otherwise "update/confirm".
      return this.ts.instant(
        this.userEmail()
          ? 'dottnet.wall.emailConfirm.title'
          : 'dottnet.wall.emailConfirm.titleMissing'
      );
    }
    return this.ts.instant('dottnet.wall.title');
  }

  // TODO move this to translation files
  getWallBody(): string {
    if (this.showEmail() && !this.hasFormGroups()) {
      // The "...body" copy interpolates {{email}}, which renders empty parens
      // when there is no email — use the dedicated "missing" copy instead.
      return this.userEmail()
        ? this.ts.instant('dottnet.wall.emailConfirm.body', { email: this.userEmail() })
        : this.ts.instant('dottnet.wall.emailConfirm.bodyMissing');
    }
    return this.listaGruppi?.includes(ListaGruppiEnum.Consents)
      ? this.ts.instant('dottnet.wall.bodyConsent')
      : this.ts.instant('dottnet.wall.bodyGeneral');
  }


  lookupCities(prefix: string) {
    this.store.dispatch(loadCityByPrefix({ prefix }));
    //this.cities.set() = this.store.select(selectCityByPrefix);
  }

  submitForm(form: FormGroup<UserForm>) {
    if (form.valid) {
      const { personalInfos, professionalInfos, consentsForm, ecmInfos } = form.getRawValue();

      const userToUpdate: User = {
        email: personalInfos.email,
        password: this.showPwd() ? personalInfos.password : this.password,
        nome: personalInfos.name,
        cognome: personalInfos.surname,
        sesso: personalInfos.gender,
        codiceFiscale: personalInfos.codiceFiscale,
        idAnagrafica: this.idAnagrafica,
        idCategoria: professionalInfos.category.idCategoria,
        idSpecializzazione: professionalInfos.specialty.idSpecializzazione,
        idComuneNascita: personalInfos.birthplace?.idComune,
        dataNascita: getDateInUTC(personalInfos.birthdate),
        privacy: this.privacy,
        marketing: evaluateBooleanFromString(consentsForm.marketing),
        profilazioneTerzi: evaluateBooleanFromString(consentsForm.profilingThirdParties),
        marketingTerzi: evaluateBooleanFromString(consentsForm.marketingThirdParties),
        profilazione: evaluateBooleanFromString(consentsForm.profiling),
        // Not asked here: null leaves the stored choice untouched.
        tracciamento: null,
        numeroIscrizioneAlbo: professionalInfos.registerForm.registerNumber,
        provinciaIscrizioneAlbo: professionalInfos.registerForm.registerProvince.siglaProvincia,
        ipAddress: '',
        cellulare: ecmInfos.cellulare,
        idTipoProfessione: ecmInfos.professionType.idTipoProfessione,
        cap: ecmInfos.cap,
        idComuneEsercizio: ecmInfos.professionPlace.idComune,
        idDisciplinaAgenas: ecmInfos.agenasDiscipline.idDisciplinaAgenas,
      };

      this.closedManually = true;
      this.store.dispatch(
        updateUser({ newUser: userToUpdate, callbackUrl: this.location.path(true) })
      );

      // TODO: clickLog success
      const clickLog: ClickLog = <ClickLog>{};
      clickLog.clickLogOperationID = ClickOperation.ModificaProfilo;
      clickLog.content = JSON.stringify(userToUpdate);
      clickLog.message = 'Wall aggiornato';
      clickLog.method = 'POST';
      clickLog.outcome = true;
      this.store.dispatch(saveClickLog({ clickLog }));
      this.dialogRef.disableClose = false;
      super.onExit();
    } else {
      form.markAllAsTouched();
    }
  }

  onExitLog() {
    super.onExit();
  }

  getListaGruppiFromArray(listaGruppi: ListaGruppi[]): ListaGruppiEnum[] {
    const resultList: ListaGruppiEnum[] = [];

    if (
      listaGruppi.filter(function (elem) {
        return elem.group === 'anagrafica';
      }).length > 0
    )
      resultList.push(ListaGruppiEnum.Personal);
    if (
      listaGruppi.filter(function (elem) {
        return elem.group === 'username_password';
      }).length > 0
    )
      resultList.push(ListaGruppiEnum.Credentials);
    if (
      listaGruppi.filter(function (elem) {
        return elem.group === 'dati_professionali';
      }).length > 0
    )
      resultList.push(ListaGruppiEnum.Professional);
    if (
      listaGruppi.filter(function (elem) {
        return elem.group === 'consensi';
      }).length > 0
    )
      resultList.push(ListaGruppiEnum.Consents);
    if (
      listaGruppi.filter(function (elem) {
        return elem.group === 'ecm';
      }).length > 0
    )
      resultList.push(ListaGruppiEnum.ECM);
    return resultList;
  }
}
